import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { useBooking } from '../../contexts/BookingContext';
import { useModal } from '../../contexts/ModalContext';
import { getBookingStatusProps } from '../../utils/helper';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg:      '#f2f6f3',
  white:   '#ffffff',
  ink:     '#0c1810',
  ink2:    '#1e3328',
  subtle:  '#4d6b58',
  muted:   '#8ba898',
  border:  '#dae6df',
  borderL: '#eaf2ec',
  green:   '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e4f5ec',
  red:     '#dc2626',
  redLt:   '#fee2e2',
  redBdr:  '#fca5a5',
  amber:   '#d97706',
  amberLt: '#fef3c7',
  amberBdr:'#fcd34d',
  blue:    '#1d4ed8',
  blueLt:  '#dbeafe',
  blueBdr: '#93c5fd',
};

// ─── Info card ────────────────────────────────────────────────────────────────
function InfoCard({ icon, label, value, color }) {
  const bg  = color === 'red'   ? T.redLt
             : color === 'amber' ? T.amberLt
             : color === 'blue'  ? T.blueLt
             : T.greenLt;
  const bdr = color === 'red'   ? T.redBdr
             : color === 'amber' ? T.amberBdr
             : color === 'blue'  ? T.blueBdr
             : T.border;
  const ic  = color === 'red'   ? T.red
             : color === 'amber' ? T.amber
             : color === 'blue'  ? T.blue
             : T.green;

  return (
    <div style={{
      background: bg, border: `1px solid ${bdr}`,
      borderRadius: 10, padding: '10px 13px',
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: ic + '22',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15,
      }}>{icon}</div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: T.ink2, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</div>
      </div>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.ink, letterSpacing: '-0.2px' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const BookingNotes = ({
  open, onClose,
  bookingId = '',
  onSave = () => {},
  action = null,
  booking = null,
}) => {
  const { updateBookingStatus, isLoading } = useBooking();
  const { openSuccessModal, openErrorModal } = useModal();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const actionLabel = action
    ? action.charAt(0).toUpperCase() + action.slice(1)
    : null;
  const title = actionLabel
    ? `Mark Booking ${actionLabel}`
    : 'Add Notes to Booking';

  const statusProps = booking ? getBookingStatusProps(booking.status, booking.status_verbose) : null;

  const handleCopy = () => {
    if (booking?.booking_id) {
      navigator.clipboard.writeText(booking.booking_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setNotes(''); setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!notes || notes.trim().length < 20 || bookingId === '') return;
    try {
      setError(null);
      const response = await updateBookingStatus(bookingId, { remarks: notes, new_status: action });
      if (response.status === 200 || response.status === 201) {
        const data = response.data;
        if (data.detail) {
          openSuccessModal('Success', data.detail);
        } else if (data.passed?.length === 1) {
          openSuccessModal('Success', 'Booking status updated successfully.');
        } else if (data.error?.length > 0) {
          setError(data.error[0]?.message || 'Failed to update booking status');
          return;
        } else {
          openErrorModal('Error', 'Failed to update booking status.');
          return;
        }
      } else {
        openErrorModal('Error', 'Failed to update booking status.');
        return;
      }
      onSave(); setNotes(''); onClose();
    } catch (err) {
      let msg = 'Failed to update booking status';
      if (err.response) {
        const { status, data } = err.response;
        if (status === 400) msg = data?.detail || data?.message || 'Invalid request.';
        else if (status === 404) msg = data?.detail || 'Booking not found.';
        else if (status === 403) msg = data?.detail || 'Permission denied.';
        else if (status === 500) msg = data?.detail || 'Server error. Try again later.';
        else msg = data?.detail || data?.message || `Request failed (${status})`;
      } else if (err.request) {
        msg = 'Network error. Check your connection.';
      } else {
        msg = err.message || 'An unexpected error occurred.';
      }
      setError(msg);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NA';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const charCount   = notes.trim().length;
  const isReady     = charCount >= 20 && bookingId !== '' && !isLoading;
  const progressPct = Math.min(100, (charCount / 20) * 100);

  const bookingDate = booking?.booking_date_str
    || (booking?.booking_date
      ? new Date(booking.booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : null);

  return (
    <>
      <style>{`
        @keyframes bnSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .bn-body { animation: bnSlideUp 220ms ease both; }
        .bn-body::-webkit-scrollbar       { width: 5px; }
        .bn-body::-webkit-scrollbar-thumb { background: #ccddd5; border-radius: 99px; }
        .bn-body::-webkit-scrollbar-track { background: transparent; }
        .bn-textarea:focus {
          outline: none;
          border-color: ${T.green} !important;
          box-shadow: 0 0 0 3px rgba(10,107,62,0.1) !important;
        }
        .bn-copy:hover { background: ${T.greenLt} !important; }
      `}</style>

      <Dialog
        open={open} onClose={handleClose} maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '16px', overflow: 'hidden', m: 0,
            width: '100%', maxWidth: 580, maxHeight: '92vh',
            boxShadow: '0 24px 80px rgba(0,0,0,0.26)',
            border: `1.5px solid ${T.border}`,
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: '92vh' }}>

          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={{
            background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            padding: '18px 22px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>✅</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>{title}</div>
                {booking?.booking_id && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    Booking {booking.booking_id}
                  </div>
                )}
              </div>
            </div>
            <button onClick={handleClose} style={{
              background: 'rgba(255,255,255,0.14)', border: 'none',
              borderRadius: 8, width: 32, height: 32, fontSize: 18,
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>

          {/* ── Scrollable body ──────────────────────────────────────── */}
          <div
            className="bn-body"
            style={{
              flex: 1, minHeight: 0, overflowY: 'auto',
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
              background: T.bg,
            }}
          >

            {/* Error */}
            {error && (
              <div style={{
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 10, padding: '10px 14px',
                fontSize: 12.5, color: T.red, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>⚠️</span> {error}
              </div>
            )}

            {/* 1 — Booking Summary */}
            <Section icon="📋" title="Booking Summary">

              {/* Status badge */}
              {statusProps && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Current Status</span>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '3px 10px', borderRadius: 20,
                    background: statusProps.color + '18',
                    border: `1px solid ${statusProps.color}40`,
                    fontSize: 11.5, fontWeight: 700, color: statusProps.color,
                  }}>
                    {statusProps.icon && <span style={{ fontSize: 12 }}>{statusProps.icon}</span>}
                    {statusProps.label}
                  </div>
                </div>
              )}

              {/* Info cards grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <InfoCard
                  icon={
                    <div style={{
                      width: 22, height: 22, borderRadius: 6,
                      background: T.green, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 800,
                    }}>
                      {getInitials(booking?.customer?.customer_name)}
                    </div>
                  }
                  label="Customer"
                  value={booking?.customer?.customer_name || 'Not available'}
                  color="green"
                />
                <InfoCard icon="🏢" label="Unit"
                  value={booking?.unit?.name || booking?.unit?.unit_number
                    || (booking?.status === 'draft' ? 'Draft — No unit' : 'Not available')}
                  color="blue"
                />
                <InfoCard icon="💰" label="Booking Amount"
                  value={booking?.booking_amount_str
                    || (booking?.booking_amount ? `₹${Number(booking.booking_amount).toLocaleString('en-IN')}` : null)}
                  color="green"
                />
                <InfoCard icon="📅" label="Booking Date"
                  value={bookingDate}
                  color="amber"
                />
              </div>

              {/* Booking ID row */}
              {booking?.booking_id && (
                <button
                  className="bn-copy"
                  onClick={handleCopy}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '9px 12px',
                    background: T.white, border: `1px solid ${T.border}`,
                    borderRadius: 9, cursor: 'pointer',
                    transition: 'background 150ms', textAlign: 'left',
                  }}
                >
                  <span style={{ fontSize: 13 }}>{copied ? '✅' : '📋'}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Booking ID</span>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink2, fontFamily: 'monospace', flex: 1 }}>
                    {booking.booking_id}
                  </span>
                  <span style={{ fontSize: 11, color: copied ? T.green : T.muted, fontWeight: 600 }}>
                    {copied ? 'Copied!' : 'Copy'}
                  </span>
                </button>
              )}
            </Section>

            {/* 2 — Notes */}
            <Section icon="📝" title="Completion Notes">
              <textarea
                autoFocus
                value={notes}
                onChange={(e) => { setNotes(e.target.value); if (error) setError(null); }}
                placeholder="Please provide detailed notes about the booking completion (minimum 20 characters)…"
                rows={5}
                disabled={isLoading}
                className="bn-textarea"
                style={{
                  width: '100%', boxSizing: 'border-box',
                  padding: '10px 12px', fontSize: 13, color: T.ink,
                  background: isLoading ? '#eff4f1' : T.white,
                  border: `1.5px solid ${error ? T.red : T.border}`,
                  borderRadius: 9, resize: 'vertical', lineHeight: 1.7,
                  transition: 'border-color 150ms', fontFamily: 'inherit',
                  cursor: isLoading ? 'not-allowed' : 'text',
                }}
              />

              {/* Progress bar + char count */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, height: 4, background: T.borderL, borderRadius: 99, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 99,
                    width: `${progressPct}%`,
                    background: charCount >= 20 ? T.green : T.amber,
                    transition: 'width 150ms, background 150ms',
                  }}/>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                  color: charCount >= 20 ? T.green : T.amber,
                }}>
                  {charCount}/20 min
                </span>
              </div>

              <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.6 }}>
                Include details about the booking outcome, customer feedback, next steps, or any other relevant information.
              </div>
            </Section>

          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div style={{
            padding: '13px 18px', flexShrink: 0,
            borderTop: `1.5px solid ${T.border}`, background: '#f8fbf9',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
          }}>
            <button
              onClick={handleClose} disabled={isLoading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 9, padding: '8px 16px',
                fontSize: 12.5, fontWeight: 700, color: T.red,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >✕ Cancel</button>

            <button
              onClick={handleSubmit} disabled={!isReady}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: !isReady
                  ? '#7ab898'
                  : 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                border: 'none', borderRadius: 9, padding: '8px 20px',
                fontSize: 12.5, fontWeight: 700, color: '#fff',
                cursor: !isReady ? 'not-allowed' : 'pointer',
                boxShadow: !isReady ? 'none' : '0 2px 10px rgba(10,107,62,0.28)',
                transition: 'all 150ms', fontFamily: 'inherit',
              }}
            >
              {isLoading ? '⏳ Submitting…' : `✅ ${actionLabel ? actionLabel + ' Booking' : 'Save Notes'}`}
            </button>
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default BookingNotes;
